// src/components/clients/CustomerSegmentationFilter.jsx
import React, { useState, useEffect } from 'react';
import Select from 'react-select';
import { get } from '../../helpers/apiHelper';
import { FaFilter, FaUsers, FaTag } from 'react-icons/fa';

// Estilos personalizados para react-select que aplican el color a cada etiqueta
const customStyles = {
  container: (provided) => ({ ...provided, width: '100%' }),
  multiValue: (provided, state) => ({
    ...provided,
    backgroundColor: state.data.color || '#777',
  }),
  multiValueLabel: (provided) => ({ ...provided, color: 'white' }),
  menu: (provided) => ({ ...provided, zIndex: 9999 }),
};

const CustomerSegmentationFilter = ({ onFilter, options, marcaId, period = '12' }) => {
  const [enhancedOptions, setEnhancedOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterType, setFilterType] = useState('basic'); // 'basic', 'rfm', 'behavior'

  useEffect(() => {
    // Por ahora, solo usar opciones básicas para mantener funcionalidad
    setEnhancedOptions(options || []);

    // TODO: Implementar fetchEnhancedSegments cuando el backend esté listo
    // if (marcaId) {
    //   fetchEnhancedSegments();
    // }
  }, [options]);

  const fetchEnhancedSegments = async () => {
    setLoading(true);
    try {
      // Intentar obtener segmentos mejorados, pero con fallback
      let enhancedData = null;
      try {
        enhancedData = await get(`clientes/analytics/enhanced-segments?period=${period}&marcaId=${marcaId}`);
      } catch (error) {
        console.log('Enhanced segments not available, using basic segments');
      }

      // Procesar opciones RFM
      const rfmOptions = [];
      if (enhancedData?.segments) {
        Object.entries(enhancedData.segments).forEach(([segmentName, customers]) => {
          rfmOptions.push({
            value: `rfm:${segmentName}`,
            label: `${segmentName} (RFM)`,
            color: getSegmentColor(segmentName),
            type: 'rfm',
            count: customers.length
          });
        });
      } else {
        // RFM por defecto si no hay datos
        const defaultRfmSegments = ['Champions', 'Loyal Customers', 'At Risk', 'Can\'t Lose', 'About to Sleep', 'Lost'];
        defaultRfmSegments.forEach(segment => {
          rfmOptions.push({
            value: `rfm:${segment}`,
            label: `${segment} (RFM)`,
            color: getSegmentColor(segment),
            type: 'rfm',
            count: 0
          });
        });
      }

      // Procesar segmentos de comportamiento
      const behaviorOptions = [];
      if (enhancedData?.rfmData) {
        const behaviorSegments = new Set();
        enhancedData.rfmData.forEach(customer => {
          if (customer.additionalSegments) {
            customer.additionalSegments.forEach(segment => {
              behaviorSegments.add(segment);
            });
          }
        });

        behaviorSegments.forEach(segment => {
          const count = enhancedData.rfmData.filter(c =>
            c.additionalSegments && c.additionalSegments.includes(segment)
          ).length;

          behaviorOptions.push({
            value: `behavior:${segment}`,
            label: `${segment} (Comportamiento)`,
            color: getSegmentColor(segment),
            type: 'behavior',
            count: count
          });
        });
      } else {
        // Segmentos de comportamiento por defecto
        const defaultBehaviorSegments = ['Fin de semana', 'Nocturnos', 'Cazapromos', 'Ticket alto', 'Express', 'Recurrentes'];
        defaultBehaviorSegments.forEach(segment => {
          behaviorOptions.push({
            value: `behavior:${segment}`,
            label: `${segment} (Comportamiento)`,
            color: getSegmentColor(segment),
            type: 'behavior',
            count: 0
          });
        });
      }

      // Combinar con opciones básicas
      const basicOptions = (options || []).map(opt => ({
        ...opt,
        type: 'basic',
        count: 0 // Se calculará dinámicamente
      }));

      setEnhancedOptions([...basicOptions, ...rfmOptions, ...behaviorOptions]);
    } catch (error) {
      console.error('Error fetching enhanced segments:', error);
      // Fallback a opciones básicas
      setEnhancedOptions(options || []);
    } finally {
      setLoading(false);
    }
  };

  const getSegmentColor = (segmentName) => {
    const colors = {
      'Champions': '#10B981',
      'Loyal Customers': '#3B82F6',
      'At Risk': '#F59E0B',
      'Can\'t Lose': '#EF4444',
      'About to Sleep': '#F97316',
      'Lost': '#6B7280',
      'Fin de semana': '#8B5CF6',
      'Nocturnos': '#6366F1',
      'Cazapromos': '#EC4899',
      'Ticket alto': '#059669',
      'Express': '#06B6D4',
      'Recurrentes': '#14B8A6',
      'Exploradores': '#F59E0B',
      'Especialistas': '#8B5CF6',
      'VIP': '#10B981',
      'Frecuentes': '#F59E0B',
      'Inactivos': '#EF4444'
    };
    return colors[segmentName] || '#6B7280';
  };

  const handleChange = (selectedOptions) => {
    // Si no se selecciona nada, se envía "All"
    if (!selectedOptions || selectedOptions.length === 0) {
      onFilter('All');
      return;
    }

    // Para simplificar, solo usar filtros básicos por ahora
    // Los filtros RFM y de comportamiento se pueden implementar cuando el backend esté listo
    const basicTags = selectedOptions
      .filter(option => option.type === 'basic')
      .map(option => option.value);

    if (basicTags.length > 0) {
      // Usar el endpoint básico existente
      onFilter(basicTags.join(','));
    } else {
      // Si solo hay filtros RFM o de comportamiento, mostrar mensaje
      console.log('Filtros RFM y de comportamiento no implementados aún en el backend');
      onFilter('All');
    }
  };

  const getFilteredOptions = () => {
    // Por ahora, solo mostrar opciones básicas
    return enhancedOptions;
  };

  return (
    <div className="mb-4">
      <div className="flex items-center justify-between mb-2">
        <label className="text-sm font-medium text-gray-700 flex items-center">
          <FaFilter className="mr-2" />
          Filtrar por Segmentación
        </label>

        {/* Selector de tipo de filtro - Simplificado por ahora */}
        <div className="flex space-x-2">
          <button
            onClick={() => setFilterType('basic')}
            className="px-2 py-1 text-xs rounded bg-blue-500 text-white"
          >
            <FaTag className="mr-1" />
            Etiquetas
          </button>
          <button
            disabled
            className="px-2 py-1 text-xs rounded bg-gray-200 text-gray-400 cursor-not-allowed"
            title="Próximamente"
          >
            <FaUsers className="mr-1" />
            RFM
          </button>
          <button
            disabled
            className="px-2 py-1 text-xs rounded bg-gray-200 text-gray-400 cursor-not-allowed"
            title="Próximamente"
          >
            <FaUsers className="mr-1" />
            Comportamiento
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center p-4 border border-gray-300 rounded">
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500 mr-2"></div>
          <span className="text-sm text-gray-500">Cargando segmentos...</span>
        </div>
      ) : (
        <Select
          isMulti
          options={getFilteredOptions()}
          onChange={handleChange}
          placeholder="Seleccione etiquetas para filtrar..."
          styles={customStyles}
          formatOptionLabel={(option) => (
            <div className="flex items-center justify-between">
              <span>{option.label}</span>
              {option.count > 0 && (
                <span className="text-xs text-gray-500 ml-2">
                  ({option.count})
                </span>
              )}
            </div>
          )}
        />
      )}

      {/* Información adicional */}
      <div className="mt-2 text-xs text-gray-500">
        Etiquetas básicas de segmentación. Los filtros RFM y de comportamiento estarán disponibles próximamente.
      </div>
    </div>
  );
};

export default CustomerSegmentationFilter;
